import { useTranslation } from 'react-i18next'
import type { ContentLocale } from '@/data/public-catalog'
import type { InfertilityProductKey } from '@/data/product-pages/infertility'

type BodyProps = { language: ContentLocale }

function useItalianProduct(productKey: InfertilityProductKey) {
  const { t } = useTranslation('common')
  return (key: string, options?: Record<string, unknown>) => t(`productPage.${productKey}.${key}`, { lng: 'it', ...options })
}

function CkupBody({ language }: BodyProps) {
  const text = useItalianProduct('ckup')
  const steps = text('steps', { returnObjects: true }) as unknown as Array<{ title: string; description: string }>
  return <section className="shell-container product-section infertility-body ckup-body" aria-labelledby="ckup-body-title">
    <div className="infertility-copy" lang={language === 'en' ? 'it' : undefined}>
      <h2 id="ckup-body-title">{text('bodyTitle')}</h2>
      <ol className="ckup-path">
        {steps.map(step => <li key={step.title}><h3>{step.title}</h3><p>{step.description}</p></li>)}
      </ol>
    </div>
  </section>
}

function Ca0Ca1Body({ language }: BodyProps) {
  const text = useItalianProduct('ca0ca1')
  return <section className="shell-container product-section infertility-body ca-body" aria-labelledby="ca-body-title">
    <div className="ca-layout" lang={language === 'en' ? 'it' : undefined}>
      <div className="infertility-copy ca-principle">
        <h2 id="ca-body-title">{text('bodyTitle')}</h2>
        <p>{text('intro')}</p>
        <p>{text('principle')}</p>
      </div>
      <aside className="ca-protocol" aria-labelledby="ca-protocol-title">
        <h3 id="ca-protocol-title">{text('protocolTitle')}</h3>
        <p>{text('protocol')}</p>
      </aside>
    </div>
  </section>
}

function R10Body({ language }: BodyProps) {
  const text = useItalianProduct('r10')
  const metrics = text('metrics', { returnObjects: true }) as unknown as Array<{ value: string; label: string }>
  return <section className="shell-container product-section infertility-body r10-body" aria-labelledby="r10-body-title">
    <div lang={language === 'en' ? 'it' : undefined}>
      <div className="r10-method">
        <div className="infertility-copy"><h2 id="r10-body-title">{text('bodyTitle')}</h2><p>{text('method')}</p></div>
        <dl className="r10-metrics">
          {metrics.map(metric => <div key={metric.value}><dt>{metric.value}</dt><dd>{metric.label}</dd></div>)}
        </dl>
      </div>
      <p className="r10-reading">{text('reading')}</p>
    </div>
  </section>
}

function R11Body({ language }: BodyProps) {
  const text = useItalianProduct('r11')
  return <section className="shell-container product-section infertility-body r11-body" aria-labelledby="r11-body-title">
    <div className="r11-layout" lang={language === 'en' ? 'it' : undefined}>
      <aside className="r11-method" aria-label={text('methodLabel')}><p>{text('methodName')}</p></aside>
      <div className="infertility-copy r11-copy">
        <h2 id="r11-body-title">{text('bodyTitle')}</h2>
        <p>{text('associations')}</p>
        <div className="r11-standardization"><p>{text('standardization')}</p><p className="r11-saving">{text('saving')}</p></div>
        <p>{text('reading')}</p>
      </div>
    </div>
  </section>
}

function X3Body({ language }: BodyProps) {
  const text = useItalianProduct('x3')
  const capabilities = text('capabilities', { returnObjects: true }) as unknown as Array<{ title: string; description: string }>
  return <section className="shell-container product-section infertility-body x3-body" aria-labelledby="x3-body-title">
    <div className="x3-register" lang={language === 'en' ? 'it' : undefined}>
      <div className="infertility-copy x3-intro"><h2 id="x3-body-title">{text('bodyTitle')}</h2><p>{text('intro')}</p></div>
      <dl className="x3-capabilities">
        {capabilities.map(capability => <div key={capability.title}><dt>{capability.title}</dt><dd>{capability.description}</dd></div>)}
      </dl>
    </div>
  </section>
}

export function InfertilityBody({ productKey, language }: { productKey: InfertilityProductKey; language: ContentLocale }) {
  if (productKey === 'ckup') return <CkupBody language={language} />
  if (productKey === 'ca0ca1') return <Ca0Ca1Body language={language} />
  if (productKey === 'r10') return <R10Body language={language} />
  if (productKey === 'r11') return <R11Body language={language} />
  return <X3Body language={language} />
}
